import { useEffect, useState, type KeyboardEvent } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Icon } from '../../../components/icons/Icon'
import { StatusPill } from '../../../components/StatusPill'
import { InlineAlert } from '../../../components/InlineAlert'
import { ErrorState } from '../../../design-system/list-view/states/ErrorState'
import { getProgram } from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import { getCloneProvenance, saveCloneProvenance } from '../../../lib/cloneProvenance'
import type { ClonedProgramView, ProgramView } from '../../../types/cartridgeStudio'
import { ParametersTab } from './ParametersTab'
import { EligibilityTab } from './EligibilityTab'
import { CalcDagTab } from './CalcDagTab'
import { AiDraftTab } from './AiDraftTab'
import { CloneProgramDialog } from './CloneProgramDialog'
import './ProgramWorkspaceScreen.css'

type WorkspaceTab = 'parameters' | 'eligibility' | 'calc-dag' | 'ai-draft'

const TABS: { id: WorkspaceTab; label: string }[] = [
  { id: 'parameters', label: 'RuleSets / Parameters' },
  { id: 'eligibility', label: 'Eligibility' },
  { id: 'calc-dag', label: 'Calc graph' },
  { id: 'ai-draft', label: 'AI draft' },
]

/**
 * `/admin/cartridge-studio/programs/:programId` — design doc §4. Header band (jurisdiction · level
 * · code, domain pill, breadcrumb) + a URL-synced (`?tab=`) tab strip so every panel is deep-linkable
 * (design-system.md §3.1 AppShell convention). Tabs are `role="tablist"`, arrow-key navigable.
 */
export function ProgramWorkspaceScreen() {
  const { programId } = useParams<{ programId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = (searchParams.get('tab') as WorkspaceTab | null) ?? 'parameters'

  const [program, setProgram] = useState<ProgramView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cloneOpen, setCloneOpen] = useState(false)

  // IB-285/IB-211 S2 (FR-M7.4) — provenance banner, layered:
  //   1. `program.clonedFromProgramId` — the SERVER fact (GET /programs/{id}). Once `program` has
  //      loaded, this is AUTHORITATIVE — null means "not a clone", full stop, even if a stale local
  //      cache entry exists (e.g. a reused test id) — the server is never second-guessed once asked.
  //   2. The clone-time cache (`lib/cloneProvenance.ts`) — used ONLY while `program` is still loading
  //      (an immediate-paint fallback so the banner doesn't flash in a beat after the fetch resolves),
  //      and as the source of richer display fields (jurisdiction/level/code/asOf/structureVersion)
  //      once the server confirms a clone AND the cached source id matches.
  //   3. `sourceLookup` (below) — a one-shot `getProgram(sourceId)` fetch for jurisdiction/level/code
  //      when the server confirms a clone but the cache has nothing for it (a different browser/
  //      session/author than the one that ran the clone).
  const cachedProvenance = programId ? getCloneProvenance(programId) : null
  const [sourceLookup, setSourceLookup] = useState<ProgramView | null>(null)

  const provenanceSourceId = program ? program.clonedFromProgramId : (cachedProvenance?.sourceProgramId ?? null)
  const cacheMatchesSource = cachedProvenance !== null && cachedProvenance.sourceProgramId === provenanceSourceId
  const serverConfirmedClone = program !== null && provenanceSourceId !== null

  const provenance = provenanceSourceId
    ? {
        sourceProgramId: provenanceSourceId,
        sourceJurisdiction: cacheMatchesSource ? cachedProvenance!.sourceJurisdiction : (sourceLookup?.jurisdiction ?? ''),
        sourceLevel: cacheMatchesSource ? cachedProvenance!.sourceLevel : (sourceLookup?.level ?? ''),
        sourceCode: cacheMatchesSource ? cachedProvenance!.sourceCode : (sourceLookup?.code ?? ''),
        sourceStructureVersion: cacheMatchesSource ? cachedProvenance!.sourceStructureVersion : null,
        asOf: cacheMatchesSource ? cachedProvenance!.asOf : null,
      }
    : null

  // Only fires once the SERVER has confirmed a clone but the local cache has nothing useful for it
  // (a different browser/session/author than the one that ran the clone) — a single GET, not a
  // list-scale N+1, and only for the one program the user is actually looking at.
  useEffect(() => {
    if (!serverConfirmedClone || cacheMatchesSource || !provenanceSourceId) {
      setSourceLookup(null)
      return
    }
    const controller = new AbortController()
    getProgram(provenanceSourceId, controller.signal)
      .then((view) => {
        if (!controller.signal.aborted) setSourceLookup(view)
      })
      .catch(() => {
        // Best-effort label enrichment only — the id-only link (provenanceSourceId) already works
        // regardless, so a failed lookup here must never block or error the workspace itself.
      })
    return () => controller.abort()
  }, [serverConfirmedClone, cacheMatchesSource, provenanceSourceId])

  useEffect(() => {
    if (!programId) return
    const controller = new AbortController()
    // React 18 StrictMode double-invokes this effect in dev (mount → cleanup → mount): the first
    // run's `controller.abort()` rejects its OWN in-flight fetch with an AbortError, which is NOT
    // an ApiError — without this guard, that stale rejection's `.catch` would overwrite the SECOND
    // (real) run's successful `setProgram` with a false "Could not load this program." error,
    // since `error` is checked before `program` in this component's render branch below. Checking
    // `controller.signal.aborted` before every setState call closes that race (mirrors the
    // `latestRequestRef` staleness guard `ProgramsGridScreen`/`ParameterEditor` use elsewhere).
    setError(null)
    setProgram(null)
    getProgram(programId, controller.signal)
      .then((view) => {
        if (controller.signal.aborted) return
        setProgram(view)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (err instanceof ApiError) setError(err.problem.detail ?? err.message)
        else setError('Could not load this program.')
      })
    return () => controller.abort()
  }, [programId])

  function setTab(next: WorkspaceTab) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('tab', next)
      return params
    })
  }

  function onTabKeyDown(event: KeyboardEvent) {
    const index = TABS.findIndex((t) => t.id === tab)
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      setTab(TABS[(index + 1) % TABS.length]!.id)
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      setTab(TABS[(index - 1 + TABS.length) % TABS.length]!.id)
    }
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => navigate(0)} />
  }

  if (!program || !programId) {
    return <div style={{ padding: 'var(--iw-space-5)', color: 'var(--iw-text-muted)' }}>Loading…</div>
  }

  return (
    <div className="iw-program-workspace">
      <div className="iw-program-workspace__breadcrumb">
        <Link to="/admin/cartridge-studio">Cartridge Studio</Link>
        <Icon name="chevron-right" size={12} />
        <span>{program.code}</span>
      </div>
      <div className="iw-program-workspace__header">
        <h1 className="iw-program-workspace__title">
          {program.jurisdiction} · {program.level} · <code>{program.code}</code>
        </h1>
        <StatusPill
          variant="neutral"
          label={program.kind === 'film' ? 'Film & Media' : 'R&D'}
          icon={program.kind === 'film' ? 'film' : 'activity'}
        />
        <button
          type="button"
          className="iw-btn iw-btn--sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setCloneOpen(true)}
        >
          <Icon name="copy" size={14} />
          Clone program…
        </button>
      </div>

      {provenance ? (
        <InlineAlert severity="info">
          Cloned from{' '}
          <Link to={`/admin/cartridge-studio/programs/${provenance.sourceProgramId}`}>
            {provenance.sourceJurisdiction ? (
              <>
                {provenance.sourceJurisdiction}
                {provenance.sourceLevel ? ` · ${provenance.sourceLevel}` : ''}
                {provenance.sourceCode ? (
                  <>
                    {' · '}
                    <code>{provenance.sourceCode}</code>
                  </>
                ) : null}
              </>
            ) : (
              provenance.sourceProgramId
            )}
          </Link>
          {provenance.asOf ? ` as of ${provenance.asOf}` : ''}
          {provenance.sourceStructureVersion !== null ? ` (source RuleSet structure v${provenance.sourceStructureVersion})` : ''}. This
          program's RuleSet and Parameters were created as <strong>draft</strong> and still need review + a
          distinct approver before they can publish.
        </InlineAlert>
      ) : null}

      <div role="tablist" aria-label="Program workspace sections" className="iw-program-workspace__tabs" onKeyDown={onTabKeyDown}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`workspace-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`workspace-tabpanel-${t.id}`}
            tabIndex={tab === t.id ? 0 : -1}
            className={tab === t.id ? 'iw-program-workspace__tab iw-program-workspace__tab--active' : 'iw-program-workspace__tab'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`workspace-tabpanel-${tab}`}
        aria-labelledby={`workspace-tab-${tab}`}
        className="iw-program-workspace__panel"
      >
        {tab === 'parameters' ? (
          <ParametersTab programId={programId} />
        ) : tab === 'eligibility' ? (
          <EligibilityTab programId={programId} />
        ) : tab === 'calc-dag' ? (
          <CalcDagTab programId={programId} />
        ) : (
          <AiDraftTab programId={programId} />
        )}
      </div>

      {cloneOpen ? (
        <CloneProgramDialog
          sourceProgram={program}
          onClose={() => setCloneOpen(false)}
          onCloned={(cloned: ClonedProgramView) => {
            saveCloneProvenance(cloned.program.id, {
              sourceProgramId: cloned.sourceProgramId,
              sourceJurisdiction: program.jurisdiction,
              sourceLevel: program.level,
              sourceCode: program.code,
              sourceStructureVersion: cloned.sourceStructureVersion,
              asOf: cloned.asOf,
              clonedAt: new Date().toISOString(),
            })
            setCloneOpen(false)
            navigate(`/admin/cartridge-studio/programs/${cloned.program.id}`)
          }}
        />
      ) : null}
    </div>
  )
}
